import { getMatches } from "actions/matches"
import { useEffect, useState, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import { selectMatches } from "selectors/matches"

import { DetailsList, TextField, TooltipHost, DatePicker, DayOfWeek, Dropdown, IconButton } from "@fluentui/react"
import { debounce } from "lodash"

import "./Dashboard.scss"

const MATCH_TYPE_OPTIONS = [
  { key: '', text: 'All' },
  { key: 'ELO', text: 'ELO' },
  { key: 'TAP', text: 'TAP' },
  { key: 'DAS', text: 'DAS' },
  { key: 'FRIENDLY', text: 'FRIENDLY' },
]

const dayPickerStrings = {
  months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  shortMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  shortDays: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
  goToToday: 'Go to today',
  prevMonthAriaLabel: 'Go to previous month',
  nextMonthAriaLabel: 'Go to next month',
  prevYearAriaLabel: 'Go to previous year',
  nextYearAriaLabel: 'Go to next year',
}

const defaultColumns = [
  {
    key: 'matchNumber',
    name: '#',
    minWidth: 55,
    maxWidth: 55,
    fieldName: 'match_number',
    isResizable: false,
  },
  {
    key: 'event',
    name: 'Event',
    minWidth: 100,
    isResizable: true,
    onRender: (item) => (
      <TooltipHost content={item?.event}><span>{item?.event}</span></TooltipHost>
    ),
  },
  {
    key: 'edition',
    name: 'Edition',
    minWidth: 50,
    maxWidth: 125,
    isResizable: true,
    onRender: (item) => (
      <TooltipHost content={item?.edition}><span>{item?.edition}</span></TooltipHost>
    ),
  },
  {
    key: 'roundGame',
    name: 'Round/Game',
    minWidth: 100,
    maxWidth: 100,
    isResizable: true,
    onRender: (item) => (
      <TooltipHost content={item?.round_game}><span>{item?.round_game}</span></TooltipHost>
    ),
  },
  {
    key: 'restreamerLocation',
    name: 'Restreamer/Location',
    minWidth: 150,
    maxWidth: 200,
    isResizable: true,
    onRender: (item) => (
      <TooltipHost content={item?.restreamer_location}><span>{item?.restreamer_location}</span></TooltipHost>
    ),
  },
  {
    key: 'matchType',
    name: 'Type',
    minWidth: 60,
    maxWidth: 80,
    fieldName: 'match_type',
    isResizable: true,
  },
  {
    key: 'date',
    name: 'Date',
    minWidth: 150,
    maxWidth: 150,
    fieldName: 'date_time',
    isResizable: false,
  },
  {
    key: 'player1',
    name: 'Player 1',
    minWidth: 150,
    maxWidth: 150,
    isResizable: true,
    onRender: (item) => {
      const classes = []
      if (item?.players[0]?.score > item?.players[1]?.score) {
        classes.push("winningPlayer")
      }
      return (
        <span className={classes.join(" ")}>{item?.players[0]?.name}</span>
      )
    }
  },
  {
    key: 'player1Score',
    name: 'Score',
    minWidth: 50,
    maxWidth: 50,
    isResizable: true,
    onRender: (item) => (
      <span>{item?.players[0]?.score}</span>
    )
  },
  {
    key: 'player2',
    name: 'Player 2',
    minWidth: 150,
    maxWidth: 150,
    isResizable: true,
    onRender: (item) => {
      const classes = []
      if (item?.players[1]?.score > item?.players[0]?.score) {
        classes.push("winningPlayer")
      }
      return (
        <span className={classes.join(" ")}>{item?.players[1]?.name}</span>
      )
    }
  },
  {
    key: 'player2Score',
    name: 'Score',
    minWidth: 50,
    maxWidth: 50,
    isResizable: true,
    onRender: (item) => (
      <span>{item?.players[1]?.score}</span>
    )
  },
]

const Dashboard = () => {
  const dispatch = useDispatch()
  const matches = useSelector(selectMatches)
  const [searchTerm, setSearchTerm] = useState('')
  const [matchType, setMatchType] = useState('')
  const [dateFrom, setDateFrom] = useState(null)
  const [dateTo, setDateTo] = useState(null)
  const [sortOrder, setSortOrder] = useState('desc')

  const buildParams = useCallback((overrides = {}) => {
    const current = {
      search: searchTerm,
      match_type: matchType,
      date_from: dateFrom ? dateFrom.toISOString() : '',
      date_to: dateTo ? dateTo.toISOString() : '',
      sort_order: sortOrder,
      ...overrides,
    }
    const params = {}
    if (current.search) params.search = current.search
    if (current.match_type) params.match_type = current.match_type
    if (current.date_from) params.date_from = current.date_from
    if (current.date_to) params.date_to = current.date_to
    if (current.sort_order) params.sort_order = current.sort_order
    return params
  }, [searchTerm, matchType, dateFrom, dateTo, sortOrder])

  // Fetch on mount and whenever filters change (except search, which is debounced)
  useEffect(() => {
    dispatch(getMatches(buildParams()))
  }, [dispatch, matchType, dateFrom, dateTo, sortOrder]) // eslint-disable-line react-hooks/exhaustive-deps

  const debouncedSearch = useCallback(
    debounce((term) => {
      dispatch(getMatches(buildParams({ search: term })))
    }, 300),
    [dispatch, buildParams]
  )

  useEffect(() => {
    return () => debouncedSearch.cancel()
  }, [debouncedSearch])

  const handleSearchChange = (event) => {
    const term = event.target.value
    setSearchTerm(term)
    debouncedSearch(term)
  }

  const handleMatchTypeChange = (_, option) => {
    if (option) setMatchType(option.key)
  }

  const toggleSort = () => {
    setSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'))
  }

  return (
    <div className="matchList">
      <div className="filterBar">
        <TextField
          className="playerSearch"
          placeholder="Search by player name..."
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <DatePicker
          firstDayOfWeek={DayOfWeek.Sunday}
          strings={dayPickerStrings}
          placeholder="From date..."
          value={dateFrom}
          onSelectDate={setDateFrom}
          styles={{ root: { minWidth: 150 } }}
        />
        <DatePicker
          firstDayOfWeek={DayOfWeek.Sunday}
          strings={dayPickerStrings}
          placeholder="To date..."
          value={dateTo}
          onSelectDate={setDateTo}
          styles={{ root: { minWidth: 150 } }}
        />
        <Dropdown
          placeholder="Type"
          selectedKey={matchType}
          options={MATCH_TYPE_OPTIONS}
          onChange={handleMatchTypeChange}
          styles={{ root: { minWidth: 120 } }}
        />
        <div className="sortToggle">
          <span className="sortLabel">Sort</span>
          <IconButton
            iconProps={{ iconName: sortOrder === 'desc' ? 'SortDown' : 'SortUp' }}
            title={sortOrder === 'desc' ? 'Newest first' : 'Oldest first'}
            onClick={toggleSort}
          />
        </div>
      </div>
      <div className="matchTable" data-is-scrollable={true}>
        <div style={{ minWidth: 1250 }}>
          <DetailsList
            items={matches}
            columns={defaultColumns}
            getKey={(item) => item.id}
            setKey="none"
            selectionMode="none"
            checkboxVisibility={2}
          />
        </div>
      </div>
    </div>
  )
}

export default Dashboard
